import React, { useState, useEffect, useRef } from "react";

const AC = typeof window !== "undefined" ? window.AudioContext || window.webkitAudioContext : null;
const GREEN = "#15803d";
const LIGHT = "#bbf7d0";
const BAR = 3;
const GAP = 2;

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

// Match the canvas's pixels to its CSS size (sharp on phones), return its context.
function sized(canvas) {
  const dpr = window.devicePixelRatio || 1;
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
  }
  const g = canvas.getContext("2d");
  g.setTransform(dpr, 0, 0, dpr, 0, 0);
  g.clearRect(0, 0, w, h);
  return { g, w, h };
}

function bar(g, x, h, level) {
  const bh = Math.max(2, Math.min(1, level) * h * 0.9);
  g.fillRect(x, (h - bh) / 2, BAR, bh);
}

// Live wave while recording: the last few seconds of loudness scroll in from
// the right. Says so when it hears nothing, so people know to check the mic.
export function LiveWave({ stream }) {
  const canvas = useRef(null);
  const [quiet, setQuiet] = useState(false);

  useEffect(() => {
    if (!stream || !AC) return;
    const ctx = new AC();
    ctx.resume && ctx.resume();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const data = new Uint8Array(analyser.fftSize);
    const levels = [];
    const began = performance.now();
    let heardAt = began;
    let lastPush = 0;
    let wasQuiet = false;
    let raf;

    const draw = (t) => {
      raf = requestAnimationFrame(draw);
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const x = (data[i] - 128) / 128;
        sum += x * x;
      }
      const rms = Math.sqrt(sum / data.length);
      if (rms > 0.015) heardAt = t;
      const isQuiet = t - began > 3000 && t - heardAt > 3000;
      if (isQuiet !== wasQuiet) setQuiet((wasQuiet = isQuiet));
      if (t - lastPush > 60) {
        levels.push(Math.min(1, rms * 5));
        lastPush = t;
      }
      if (!canvas.current) return;
      const { g, w, h } = sized(canvas.current);
      const n = Math.floor(w / (BAR + GAP));
      const shown = levels.slice(-n);
      g.fillStyle = GREEN;
      shown.forEach((lv, i) => bar(g, w - (shown.length - i) * (BAR + GAP), h, lv));
    };
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      ctx.close();
    };
  }, [stream]);

  return (
    <div>
      <canvas ref={canvas} className="w-full h-14 block" aria-label="Live recording level" />
      {quiet && (
        <p className="text-xs text-earth-600 mt-1">
          We can't hear anything yet. Check that your microphone is on and not muted.
        </p>
      )}
    </div>
  );
}

const PEAKS = 140;

// Recording's waveform with play/pause: the part already played fills in
// green as it plays; tap or click the wave to jump to that spot.
export function WavePlayer({ blob }) {
  const [url, setUrl] = useState(null);
  const [peaks, setPeaks] = useState(null);
  const [decoded, setDecoded] = useState(0);
  const [audioDur, setAudioDur] = useState(0);
  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const audio = useRef(null);
  const canvas = useRef(null);

  useEffect(() => {
    const u = URL.createObjectURL(blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [blob]);

  // Read the whole file once for its shape (skip very large uploads).
  useEffect(() => {
    let cancelled = false;
    setPeaks(null);
    setDecoded(0);
    (async () => {
      try {
        if (!AC || blob.size > 12 * 1024 * 1024) throw new Error("skip");
        const ctx = new AC();
        const buf = await new Promise((resolve, reject) =>
          blob.arrayBuffer().then((ab) => {
            const p = ctx.decodeAudioData(ab, resolve, reject);
            if (p && p.then) p.then(resolve, reject);
          }, reject)
        );
        ctx.close();
        const ch = buf.getChannelData(0);
        const step = Math.max(1, Math.floor(ch.length / PEAKS));
        const out = [];
        for (let i = 0; i < PEAKS; i++) {
          let max = 0;
          for (let j = i * step; j < Math.min(ch.length, (i + 1) * step); j++) {
            const v = Math.abs(ch[j]);
            if (v > max) max = v;
          }
          out.push(max);
        }
        const top = Math.max(...out, 0.01);
        if (!cancelled) {
          setPeaks(out.map((v) => v / top));
          setDecoded(buf.duration);
        }
      } catch {
        if (!cancelled) setPeaks(new Array(PEAKS).fill(0.35));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [blob]);

  // Browser recordings (webm) often report no length, so prefer the decoded one.
  const duration = decoded || audioDur;

  useEffect(() => {
    if (!canvas.current || !peaks) return;
    const { g, w, h } = sized(canvas.current);
    const n = Math.floor(w / (BAR + GAP));
    const done = duration ? time / duration : 0;
    for (let i = 0; i < n; i++) {
      const lv = peaks[Math.floor((i / n) * peaks.length)];
      g.fillStyle = i / n < done ? GREEN : LIGHT;
      bar(g, i * (BAR + GAP), h, 0.12 + lv * 0.88);
    }
  }, [peaks, time, duration]);

  useEffect(() => {
    if (!playing) return;
    let raf;
    const tick = () => {
      if (audio.current) setTime(audio.current.currentTime);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing]);

  const toggle = () => {
    const a = audio.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => setPlaying(false));
    else a.pause();
  };

  const seek = (e) => {
    const a = audio.current;
    if (!a || !duration) return;
    const r = e.currentTarget.getBoundingClientRect();
    const t = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * duration;
    a.currentTime = t;
    setTime(t);
  };

  return (
    <div className="flex items-center gap-3 bg-white rounded-lg border border-sritaru-200 px-3 py-2">
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? "Pause" : "Play"}
        className="shrink-0 w-10 h-10 rounded-full bg-sritaru-600 hover:bg-sritaru-700 text-white flex items-center justify-center"
      >
        {playing ? (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="2" y="1" width="3.5" height="12" rx="1" /><rect x="8.5" y="1" width="3.5" height="12" rx="1" /></svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><path d="M3 1.5v11a.5.5 0 0 0 .77.42l8.5-5.5a.5.5 0 0 0 0-.84l-8.5-5.5A.5.5 0 0 0 3 1.5z" /></svg>
        )}
      </button>
      <canvas ref={canvas} onClick={seek} className="flex-1 min-w-0 h-12 block cursor-pointer" aria-label="Recording waveform" />
      <span className="shrink-0 font-mono text-xs text-gray-600 w-20 text-right">
        {fmt(time)} / {duration ? fmt(duration) : "--:--"}
      </span>
      {url && (
        <audio
          ref={audio}
          src={url}
          preload="metadata"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => {
            setPlaying(false);
            setTime(0);
          }}
          onLoadedMetadata={(e) => isFinite(e.currentTarget.duration) && setAudioDur(e.currentTarget.duration)}
          className="hidden"
        />
      )}
    </div>
  );
}
